
import logo from './assets/ic_launcher_foreground_transparent.webp';
import googlePlayBadge from './assets/GetItOnGooglePlay_Badge_Web_color_English.png';
import appStoreBadge from './assets/download-on-the-app-store.svg';
import './App.css';
import { BrowserRouter as Router, Route, Routes, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LegalCenter, LegalDocumentPage } from './pages/Legal';
import Home from './pages/Home';
import About from './pages/About';
import Help from './pages/Help';
import Faq from './pages/Faq';
import { useEffect, useRef, useState } from 'react';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.kjm.stepcounter';
const IOS_APP_STORE_URL = 'https://apps.apple.com/app/id6779684228';

const detectDevicePlatform = () => {
  const ua = window.navigator.userAgent.toLowerCase();
  const uaDataPlatform = window.navigator.userAgentData?.platform?.toLowerCase();
  const platformProbe = `${ua} ${uaDataPlatform || ''}`;
  const isTouchMac = /macintosh/.test(platformProbe) && 'ontouchend' in window;

  if (/android/.test(platformProbe)) return 'android';
  if (/iphone|ipad|ipod/.test(platformProbe) || isTouchMac) return 'ios';
  return 'desktop';
};

const openExternalStore = (url) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const upsertMeta = (name, content) => {
  let element = document.querySelector(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

function RouteMetadata() {
  const location = useLocation();

  useEffect(() => {
    const isPrivateLegalRoute =
      location.pathname === '/privacy-policy' || location.pathname.startsWith('/legal');

    if (isPrivateLegalRoute) {
      upsertMeta('robots', 'noindex, noarchive, nosnippet');
    } else {
      upsertMeta('robots', 'index, follow');
    }
  }, [location.pathname]);

  return null;
}

// Scroll to top button component
// Scroll to top button component with icon
function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!visible) return null;
  return (
    <button className="scroll-to-top-btn" onClick={scrollToTop} aria-label="Scroll to top">
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{display:'block',margin:'auto'}}>
        <path d="M14 9L14 19" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
        <path d="M9 14L14 9L19 14" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    </button>
  );
}

// Custom navigation link component that handles scroll and transitions
function NavLink({ to, children, className, onClick }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    
    // If clicking the current page, scroll to top
    if (location.pathname === to) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Trigger fade out
    document.querySelector('.main-content').classList.add('page-transition-exit');
    
    // Navigate after fade out
    setTimeout(() => {
      navigate(to);
      window.scrollTo(0, 0);
    }, 300);
    
    if (onClick) onClick();
  };
  
  return (
    <Link to={to} className={className} onClick={handleClick}>
      {children}
    </Link>
  );
}

function AppContent() {
  const location = useLocation();
  const [, setIsTransitioning] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showStorePicker, setShowStorePicker] = useState(false);
  const menuButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const storePickerCloseRef = useRef(null);
  
  useEffect(() => {
    // Fade in on route change
    setIsTransitioning(true);
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.classList.remove('page-transition-exit');
      mainContent.classList.add('page-transition-enter');
    }
    
    const timer = setTimeout(() => {
      setIsTransitioning(false);
      if (mainContent) {
        mainContent.classList.remove('page-transition-enter');
      }
      
      // Handle hash navigation after page transition
      if (location.hash) {
        const element = document.querySelector(location.hash);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowStorePicker(false);
  }, [location.pathname]);

  useEffect(() => {
    if (showStorePicker) {
      storePickerCloseRef.current?.focus();
    }
  }, [showStorePicker]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;

      if (showStorePicker) {
        closeStorePicker();
        return;
      }

      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen, showStorePicker]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((isOpen) => !isOpen);
  };

  const closeStorePicker = () => {
    setShowStorePicker(false);
    previousFocusRef.current?.focus?.();
  };

  const openStorePicker = () => {
    previousFocusRef.current = document.activeElement;
    const platform = detectDevicePlatform();
    if (platform === 'android') {
      openExternalStore(PLAY_STORE_URL);
      setMobileMenuOpen(false);
      return;
    }
    if (platform === 'ios') {
      openExternalStore(IOS_APP_STORE_URL);
      setMobileMenuOpen(false);
      return;
    }
    setShowStorePicker(true);
    setMobileMenuOpen(false);
  };
  
  return (
    <div className="App">
      <RouteMetadata />
      <header className="fixed-header">
        <div className="header-content">
          <NavLink to="/" className="header-left">
            <img src={logo} className="header-logo" alt="Simple Step Counter logo" />
            <span className="app-name">Simple Step Counter</span>
          </NavLink>
          <nav className="header-nav desktop-nav">
            <NavLink to="/" className="nav-link">Home</NavLink>
            <NavLink to="/about" className="nav-link">About</NavLink>
            <NavLink to="/faq" className="nav-link">FAQ</NavLink>
            <NavLink to="/help" className="nav-link">Help</NavLink>
            <button
              type="button"
              className="nav-button nav-button-full desktop-nav"
              onClick={openStorePicker}
            >
              Download
            </button>
          </nav>
          <button 
            ref={menuButtonRef}
            className="hamburger-menu" 
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-controls="mobile-navigation"
            aria-expanded={mobileMenuOpen}
          >
            <span className={`hamburger-icon ${mobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          </div>
        </header>
        
        {/* Mobile Menu Overlay */}
        <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={toggleMobileMenu}></div>
        <nav
          id="mobile-navigation"
          className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}
          aria-hidden={!mobileMenuOpen}
        >
          <NavLink to="/" className="mobile-nav-link" onClick={toggleMobileMenu}>Home</NavLink>
          <NavLink to="/about" className="mobile-nav-link" onClick={toggleMobileMenu}>About</NavLink>
          <NavLink to="/faq" className="mobile-nav-link" onClick={toggleMobileMenu}>FAQ</NavLink>
          <NavLink to="/help" className="mobile-nav-link" onClick={toggleMobileMenu}>Help</NavLink>
          <button
            type="button"
            className="mobile-nav-link"
            onClick={openStorePicker}
          >
            Download
          </button>
        </nav>

        <main className="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/help" element={<Help />} />
            <Route path="/privacy-policy" element={<Navigate to="/legal/android/privacy-policy" replace />} />
            <Route path="/legal" element={<LegalCenter />} />
            <Route path="/legal/:platform/:documentSlug" element={<LegalDocumentPage />} />
          </Routes>
          <ScrollToTopButton />
        </main>
        <footer className="footer">
          <div className="footer-content">
            <div className="footer-section">
              <h3>Simple Step Counter</h3>
              <p>Steps. Simplified.</p>
            </div>
            <div className="footer-section">
              <NavLink to="/legal" className="footer-link footer-heading-link">Legal & Privacy</NavLink>
            </div>
            <div className="footer-section">
              <button type="button" className="footer-download-title" onClick={openStorePicker}>
                Download
              </button>
              <div className="footer-download-links">
              <a 
                href={PLAY_STORE_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer-link"
              >
                Google Play Store
              </a>
              <a 
                href={IOS_APP_STORE_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer-link"
              >
                Apple App Store
              </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            {/* <p>Made in the EU <img src={require('./assets/Flag_of_Europe.webp')} alt="EU flag" className="eu-flag-img" /></p> */}
          </div>
        </footer>
        {showStorePicker && (
          <div className="store-picker-backdrop" onClick={closeStorePicker} role="presentation">
            <div
              className="store-picker-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="store-picker-title"
              onClick={(event) => event.stopPropagation()}
            >
              <h3 id="store-picker-title">Choose your platform</h3>
              <div className="store-picker-actions">
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-badge-link store-picker-badge"
                >
                  <img src={googlePlayBadge} alt="Get it on Google Play" className="google-play-badge" />
                </a>
                <a
                  href={IOS_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store-badge-link store-picker-badge"
                >
                  <img src={appStoreBadge} alt="Download on the App Store" className="app-store-badge" />
                </a>
              </div>
              <button
                ref={storePickerCloseRef}
                type="button"
                className="store-picker-close"
                onClick={closeStorePicker}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
